import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * The small Markdown the help articles are written in (see help/types.ts):
 * ## and ### headings, paragraphs, - and 1. lists, > notes, | tables |,
 * **bold**, *italic*, `code`, and [links](/path). A link starting with / uses
 * the router, so opening a screen from help never reloads the app -- which
 * matters on a phone with one bar of signal.
 *
 * Deliberately not a library: the articles are ours, the subset is fixed, and
 * this is the whole of it.
 */

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*)/g

function inline(text: string, onNavigate?: () => void): ReactNode[] {
  return text
    .split(INLINE)
    .filter(Boolean)
    .map((part, i) => {
      if (part.startsWith('**')) return <strong key={i}>{inline(part.slice(2, -2), onNavigate)}</strong>
      if (part.startsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>
      const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
      if (link) {
        const [, label, href] = link
        return href.startsWith('/') ? (
          <Link key={i} to={href} onClick={onNavigate}>
            {label}
          </Link>
        ) : (
          <a key={i} href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        )
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>
      return <Fragment key={i}>{part}</Fragment>
    })
}

function cells(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim())
}

const BLOCK_START = /^(#{1,3}\s|\||>|\s*(-|\d+\.)\s)/

export function Markdown({ source, onNavigate }: { source: string; onNavigate?: () => void }) {
  const lines = source.replace(/^\n+|\n+$/g, '').split('\n')
  const out: ReactNode[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) {
      i++
      continue
    }
    const h = line.match(/^(#{1,3})\s+(.*)$/)
    if (h) {
      const Tag = h[1].length <= 2 ? 'h3' : 'h4'
      out.push(<Tag key={i}>{inline(h[2], onNavigate)}</Tag>)
      i++
      continue
    }
    if (line.trim().startsWith('|')) {
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) rows.push(lines[i++])
      const [head, , ...body] = rows
      out.push(
        <div key={i} className="md-table">
          <table>
            <thead>
              <tr>
                {cells(head).map((c, j) => (
                  <th key={j}>{inline(c, onNavigate)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, k) => (
                <tr key={k}>
                  {cells(r).map((c, j) => (
                    <td key={j}>{inline(c, onNavigate)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }
    if (/^\s*(-|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line)
      const items: string[] = []
      while (i < lines.length && /^\s*(-|\d+\.)\s+/.test(lines[i])) {
        let item = lines[i++].replace(/^\s*(-|\d+\.)\s+/, '')
        // A wrapped list item: indented continuation lines join the item.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*(-|\d+\.)\s+/.test(lines[i])) {
          item += ' ' + lines[i++].trim()
        }
        items.push(item)
      }
      const L = ordered ? 'ol' : 'ul'
      out.push(
        <L key={i}>
          {items.map((it, k) => (
            <li key={k}>{inline(it, onNavigate)}</li>
          ))}
        </L>,
      )
      continue
    }
    if (line.startsWith('>')) {
      const quote: string[] = []
      while (i < lines.length && lines[i].startsWith('>')) quote.push(lines[i++].replace(/^>\s?/, ''))
      out.push(
        <div key={i} className="md-note">
          {inline(quote.join(' '), onNavigate)}
        </div>,
      )
      continue
    }
    const para: string[] = []
    while (i < lines.length && lines[i].trim() && !BLOCK_START.test(lines[i])) para.push(lines[i++])
    out.push(<p key={i}>{inline(para.join(' '), onNavigate)}</p>)
  }
  return <div className="md">{out}</div>
}
